import React, { useEffect, useRef, useState } from 'react';
import { View, Image, Dimensions, Animated, PanResponder, TouchableOpacity } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import PropTypes from 'prop-types';

import { VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../../FrameworkTestKeys';
import { PositionsProvider, TooltipsIds } from '../../../Services/PositionsProvider';
import { Images } from '../../../Themes';
import Badge from '../Components/Badge/Badge';
import { TRAY_TYPES } from '../Utils/TrayTypesConstants';

import styles from './TobiInteractive.Styles';

const SCREEN_WIDTH = Dimensions.get('window').width;
const INITIAL_CONTAINER_SIZE = SCREEN_WIDTH / 5.294;
const FINAL_CONTAINER_SIZE = SCREEN_WIDTH - SCREEN_WIDTH / 10;
const MAX_TRANSLATEX = SCREEN_WIDTH - SCREEN_WIDTH / 1.8;

function TobiInteractive({
  welcomeMessage,
  notifications,
  trayIcon,
  customTrayIconStyle,
  willUnmount,
  welcomeMessageBreakTime,
  notificationsBreakTime,
  showModal,
  hideModal,
  allowFontScaling,
  style,
  dropTray,
  upTray,
  trayType,
}) {
  const [showNotification, setShowNotification] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const trayBreakTime =
    notifications && notifications.length ? notificationsBreakTime : welcomeMessageBreakTime;
  const message =
    showNotification && notifications && notifications.length
      ? notifications[0].message
      : welcomeMessage;
  const isTrayMainType = trayType === TRAY_TYPES.MAIN;
  const trayAnimation = useRef(new Animated.Value(INITIAL_CONTAINER_SIZE)).current;
  const tobiTranslateX = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;
  const timeoutRef = useRef(null);
  const isTobiRight = useRef(false);
  const tobiRef = useRef();

  useEffect(() => {
    PositionsProvider.measure({
      id: TooltipsIds.TOBI,
      ref: tobiRef.current,
      isAbsolute: true,
    });
  }, []);

  useEffect(() => {
    isTrayMainType ? setShowNotification(false) : setShowNotification(true);
  }, [isTrayMainType]);

  useEffect(() => {
    if (dropTray) {
      handelExpandAnimation();
    }
  }, [dropTray]);

  useEffect(() => {
    if (upTray) {
      setDisabled(false);
      trayAnimation.stopAnimation();
      setTimeout(() => {
        handleCenterAnimation();
      }, 0);
    }
  }, [upTray]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onResponderTerminationRequest: () => false,
      onMoveShouldSetPanResponder: (evt, gestureState) =>
        gestureState.dx > 5 || gestureState.dx < -5,
      onPanResponderGrant: () => {
        tobiTranslateX.setOffset({
          x: tobiTranslateX.x._value,
          y: tobiTranslateX.y._value,
        });
        Animated.timing(trayAnimation, {
          toValue: FINAL_CONTAINER_SIZE,
          duration: 50,
          useNativeDriver: false,
        }).start();
      },
      onPanResponderMove: (_, gesture) => {
        if (gesture.moveX > 60 && gesture.moveX < SCREEN_WIDTH - 20) {
          tobiTranslateX.setValue({ x: gesture.dx, y: 0 });
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        if (gestureState.dx > 0) {
          handelMinimizeAnimation();
        } else {
          Animated.timing(tobiTranslateX, {
            toValue: { x: 0, y: 0 },
            duration: 100,
            useNativeDriver: false,
          }).start(({ finished }) => {
            if (finished) {
              isTobiRight.current = false;
              handelMinimizeAnimation(trayBreakTime);
            }
          });
        }

        tobiTranslateX.flattenOffset();
      },
    }),
  ).current;

  const handelMinimizeAnimation = (time = 0) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      Animated.parallel([
        Animated.timing(trayAnimation, {
          toValue: SCREEN_WIDTH / 3,
          duration: 400,
          useNativeDriver: false,
        }),
        Animated.timing(tobiTranslateX, {
          toValue: { x: MAX_TRANSLATEX, y: 0 },
          duration: 400,
          useNativeDriver: false,
        }),
      ]).start(({ finished }) => {
        isTobiRight.current = finished;
      });
    }, time);
  };

  const handelExpandAnimation = () => {
    setTimeout(() => {
      Animated.timing(trayAnimation, {
        toValue: FINAL_CONTAINER_SIZE,
        duration: 400,
        delay: 500,
        useNativeDriver: false,
      }).start(() => {
        handelMinimizeAnimation(trayBreakTime);
      });
    }, 0);
  };

  const handleCenterAnimation = () => {
    setShowNotification(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    handelOpenModal();
  };

  const onTobiPress = () => {
    setShowNotification(false);
    setDisabled(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (isTobiRight.current) {
      const callBack = () =>
        showModal(true, tobiTranslateX, () => {
          handelMinimizeAnimation();
          setDisabled(false);
        });
      handelOpenModal(callBack);
    } else {
      const callBack = () =>
        showModal(true, tobiTranslateX, () => {
          dropTray && handelExpandAnimation();
          setDisabled(false);
        });
      handelOpenModal(callBack);
    }
  };

  const handelOpenModal = callBack => {
    Animated.parallel([
      Animated.timing(tobiTranslateX, {
        toValue: { x: 0, y: 0 },
        duration: 300,
        useNativeDriver: false,
      }),
      Animated.timing(trayAnimation, {
        toValue: INITIAL_CONTAINER_SIZE,
        duration: 200,
        useNativeDriver: false,
      }),
    ]).start(({ finished }) => {
      if (finished) {
        callBack && callBack();
      }
    });
  };

  const theme = useTheme();

  const opacity = trayAnimation.interpolate({
    inputRange: [INITIAL_CONTAINER_SIZE, FINAL_CONTAINER_SIZE * 0.99, FINAL_CONTAINER_SIZE],
    outputRange: [0, 0, 1],
  });

  const paddingLeft = trayAnimation.interpolate({
    inputRange: [INITIAL_CONTAINER_SIZE, FINAL_CONTAINER_SIZE],
    outputRange: [0, 0],
  });

  const tobiTranslateXInter = tobiTranslateX.x.interpolate({
    inputRange: [0, SCREEN_WIDTH - 20],
    outputRange: [0, SCREEN_WIDTH - 20],
    extrapolate: 'clamp',
  });
  const pan = disabled ? null : panResponder.panHandlers;

  return (
    <View
      collapsable={false}
      style={[styles.outerContainer, style]}
      // {...pan}
      pointerEvents="box-none"
      ref={tobiRef}
    >
      <Animated.View
        style={[
          styles.container(theme),
          isTrayMainType && styles.containerBorder(theme),
          {
            width: SCREEN_WIDTH >= 600 ? 90 : trayAnimation,
            paddingLeft,
            transform: [{ translateX: tobiTranslateXInter }, { translateY: tobiTranslateX.y }],
          },
        ]}
      >
        {notifications && notifications.length && showNotification ? (
          <Badge
            text={'!'}
            containerStyle={styles.badgeStyle}
            allowFontScaling={allowFontScaling}
          />
        ) : null}

        <TouchableOpacity
          disabled={disabled}
          onPress={onTobiPress}
          style={isTrayMainType ? styles.tobiContainerStyle : null}
        >
          {trayIcon ? (
            <View
              style={[styles.customTrayIcon, customTrayIconStyle]}
              testID={FrameworkTestKeys.TrayCustomIcon}
            >
              {trayIcon}
            </View>
          ) : (
            <Image
              source={Images.toBi}
              style={styles.tobiStyle(isTrayMainType)}
              testID={FrameworkTestKeys.TrayTobiIcon}
            />
          )}
        </TouchableOpacity>

        <Animated.View style={[styles.textView, { opacity }]}>
          <VFText
            i18nKey={message}
            testID={FrameworkTestKeys.TobiMessage}
            style={styles.text}
            allowFontScaling={allowFontScaling}
            numberOfLines={2}
          />
        </Animated.View>
      </Animated.View>
    </View >
  );
}

TobiInteractive.defaultProps = {
  willUnmount: () => { },
};

TobiInteractive.propTypes = {
  welcomeMessage: PropTypes.string,
  notifications: PropTypes.array,
  trayIcon: PropTypes.element,
  willUnmount: PropTypes.func,
  welcomeMessageBreakTime: PropTypes.number,
  notificationsBreakTime: PropTypes.number,
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
  allowFontScaling: PropTypes.bool,
  style: ViewPropTypes.style,
  dropTray: PropTypes.bool,
  upTray: PropTypes.bool,
  trayType: PropTypes.string,
  customTrayIconStyle: ViewPropTypes.style,
};

export default TobiInteractive;
